import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { StarRating } from "@/components/StarRating";
import { X } from "lucide-react";

// 5 critères — notation candidat → client (Feb 2026 bidirectionnel)
const CRITERIA = [
    { key: "welcome", label: "Accueil sur site" },
    { key: "brief_conformity", label: "Conformité au brief" },
    { key: "schedule_respect", label: "Respect des horaires" },
    { key: "payment", label: "Paiement (délai & conformité)" },
    { key: "environment", label: "Environnement de travail" },
];

const EMPTY = { welcome: 0, brief_conformity: 0, schedule_respect: 0, payment: 0, environment: 0 };

/**
 * Modal soumission notation candidat → client (5 étoiles + would_work_again + comment).
 * Props: open (bool), pending (obj|null), onClose(fn), onSubmitted(fn).
 */
export default function ClientEvaluationModal({ open, pending, onClose, onSubmitted }) {
    const [ratings, setRatings] = useState(EMPTY);
    const [wouldWorkAgain, setWouldWorkAgain] = useState(true);
    const [comment, setComment] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (open) {
            setRatings(EMPTY);
            setWouldWorkAgain(true);
            setComment("");
            setError("");
        }
    }, [open, pending?.application_id]);

    if (!open || !pending) return null;

    const allSet = CRITERIA.every((c) => ratings[c.key] > 0);
    const clientDisplay = pending.client?.company_name || pending.client?.full_name || "Client";

    const submit = async () => {
        if (!allSet) { setError("Merci de noter les 5 critères."); return; }
        setSubmitting(true);
        setError("");
        try {
            await api.post("/client-ratings", {
                application_id: pending.application_id,
                welcome: ratings.welcome,
                brief_conformity: ratings.brief_conformity,
                schedule_respect: ratings.schedule_respect,
                payment: ratings.payment,
                environment: ratings.environment,
                would_work_again: wouldWorkAgain,
                comment,
            });
            if (onSubmitted) onSubmitted();
            onClose();
        } catch (e) {
            setError(e?.response?.data?.detail || "Erreur lors de l'envoi.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-[#0B192C]/70 backdrop-blur-sm flex items-center justify-center p-4"
            data-testid="client-evaluation-modal"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between items-start p-6 border-b border-slate-100">
                    <div>
                        <div className="overline text-[#F5A623]">Notation du client</div>
                        <h2 className="font-display text-xl mt-1">
                            {clientDisplay}
                        </h2>
                        <div className="text-xs text-slate-500 mt-1">
                            {pending.mission_title} · {pending.mission_date} · {pending.mission_city}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-slate-400 hover:text-slate-600"
                        data-testid="client-eval-close"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
                    <p className="text-xs text-slate-500 leading-relaxed">
                        Votre notation aide les autres prestataires Renforteo à choisir leurs
                        missions en toute transparence. Seule la moyenne agrégée est visible ;
                        votre commentaire reste privé (Renforteo uniquement).
                    </p>

                    {CRITERIA.map((c) => (
                        <div key={c.key} className="flex justify-between items-center gap-3 flex-wrap">
                            <div className="text-sm font-semibold text-[#0B192C]">{c.label}</div>
                            <StarRating
                                value={ratings[c.key]}
                                onChange={(n) => setRatings((r) => ({ ...r, [c.key]: n }))}
                                testIdPrefix={`client-eval-${c.key}`}
                            />
                        </div>
                    ))}

                    <div className="pt-2 border-t border-slate-100">
                        <div className="text-sm font-semibold mb-2">
                            Retravailleriez-vous avec ce client ?
                        </div>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => setWouldWorkAgain(true)}
                                className={`px-4 py-2 rounded-full text-sm font-semibold border-2 transition ${
                                    wouldWorkAgain
                                        ? "border-[#F5A623] bg-[#F5A623]/10 text-[#0B192C]"
                                        : "border-slate-200 text-slate-500"
                                }`}
                                data-testid="client-eval-recommend-yes"
                            >
                                Oui, sans hésiter
                            </button>
                            <button
                                type="button"
                                onClick={() => setWouldWorkAgain(false)}
                                className={`px-4 py-2 rounded-full text-sm font-semibold border-2 transition ${
                                    !wouldWorkAgain
                                        ? "border-red-400 bg-red-50 text-red-700"
                                        : "border-slate-200 text-slate-500"
                                }`}
                                data-testid="client-eval-recommend-no"
                            >
                                Non
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className="text-sm font-semibold block mb-2">
                            Commentaire <span className="text-xs text-slate-400 font-normal">(facultatif, visible uniquement par Renforteo)</span>
                        </label>
                        <textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            rows={3}
                            maxLength={2000}
                            className="w-full border border-slate-200 rounded-lg p-3 text-sm focus:border-[#F5A623] focus:ring-1 focus:ring-[#F5A623]/40 outline-none"
                            placeholder="Un point remarquable à signaler (accueil, brief, paiement) ?"
                            data-testid="client-eval-comment"
                        />
                    </div>

                    {error && (
                        <div className="text-sm text-red-600" data-testid="client-eval-error">{error}</div>
                    )}
                </div>

                <div className="p-4 border-t border-slate-100 flex justify-end gap-2 bg-slate-50">
                    <button
                        onClick={onClose}
                        className="btn-ghost-navy text-sm"
                        data-testid="client-eval-cancel"
                    >
                        Annuler
                    </button>
                    <button
                        onClick={submit}
                        disabled={!allSet || submitting}
                        className="btn-gold text-sm disabled:opacity-50"
                        data-testid="client-eval-submit"
                    >
                        {submitting ? "Envoi…" : "Envoyer la notation"}
                    </button>
                </div>
            </div>
        </div>
    );
}
